import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/StatusBadge";
import { createFormatter, type Jurisdiction } from "@/lib/formatters";
import type { ScenarioStatus } from "@/lib/scenarios";
import type { DashboardSummary } from "@/lib/calculationEngine";
import type { PurposeSelection } from "@/lib/purpose";
import { PURPOSE_CATEGORIES } from "@/lib/purpose";
import { Calendar, Target, TrendingUp } from "lucide-react";

interface Props {
  loanName: string | null;
  loanId: string;
  status: ScenarioStatus;
  dashboard: DashboardSummary;
  purpose: PurposeSelection;
  jurisdiction: Jurisdiction;
  /** Optional override for progress (0-100). Defaults to 0 if not computed. */
  progressPct?: number;
}

function purposeLabel(p: PurposeSelection): string | null {
  if (!p || !p.categoryId) return null;
  const cat = PURPOSE_CATEGORIES.find((c) => c.id === p.categoryId);
  if (!cat) return null;
  if (p.specificId) {
    const spec = cat.options.find((o) => o.id === p.specificId);
    if (spec) return `${cat.label} — ${spec.label}`;
  }
  return cat.label;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}

export function LoanHeaderCard({
  loanName, loanId, status, dashboard, purpose, jurisdiction, progressPct = 0,
}: Props) {
  const { fmt } = createFormatter(jurisdiction);
  const pl = purposeLabel(purpose);
  const completion = dashboard.policyLoanPayoffDate || dashboard.standardLoanPayoffDate;

  return (
    <Card className="border-primary/20">
      <CardContent className="pt-5 pb-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg sm:text-xl font-semibold truncate">
                {loanName || "Untitled scenario"}
              </h2>
              <StatusBadge status={status} />
            </div>
            <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline" className="font-mono">{loanId}</Badge>
              {pl && <span className="truncate">{pl}</span>}
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-baseline justify-between text-xs mb-1">
            <span className="text-muted-foreground">Repayment progress</span>
            <span className="font-medium tabular-nums">{Math.round(progressPct)}%</span>
          </div>
          <Progress value={progressPct} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
          <Metric
            icon={<Target className="h-4 w-4 text-[hsl(var(--status-active))]" />}
            label="Target Payment"
            value={fmt(dashboard.targetPaymentPerPeriod)}
          />
          <Metric
            icon={<TrendingUp className="h-4 w-4 text-[hsl(var(--status-success))]" />}
            label="Family Financing Margin™"
            value={fmt(dashboard.additionalIntendedFinancingMargin)}
          />
          <Metric
            icon={<Calendar className="h-4 w-4 text-[hsl(var(--status-info))]" />}
            label="Est. Completion"
            value={fmtDate(completion)}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md border bg-card/50 p-3">
      <div className="mt-0.5">{icon}</div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-sm sm:text-base font-semibold tabular-nums truncate">{value}</div>
      </div>
    </div>
  );
}